import React, { useEffect, useMemo, useState } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import hljs from "highlight.js";
import "highlight.js/styles/github-dark.css";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { fetchGitStatus } from "@/store/gitStatusSlice";
import {
  getUnexpiredToken,
  useActiveProject,
  useCanvasId,
} from "@/utils/utils";
import { useStartServer } from "@/utils/devServerUtils";
import { CursorType, setCursorType } from "@/store/globalSettingsSlice";
import { Button } from "../ui/button";
import useLayoutStore, { LeftTab } from "@/store/zustand/layoutStore";
import { ProjectRunMode, ReactFramework } from "@/utils/templateProjects";
import {
  DevtoolStatus,
  EditorContext,
  useCodebaseStore,
} from "@/store/zustand/codebaseStore";
import useLocalMode from "@/hooks/useLocalMode";
import {
  useAiContextStore,
  useIsChatLoading,
} from "@/store/zustand/aiContextStore";
import { useAiActionStore } from "@/store/zustand/aiActionStore";
import { show } from "@intercom/messenger-js-sdk";
import { track } from "@/utils/events/tracker";
import { useSession } from "@clerk/clerk-react";

enum InstallationPhase {
  INSTALLING,
  INITIALIZING,
}

enum InstallationFramework {
  // CRA = "CRA",
  NEXT_JS = "NEXT_JS",
  NEXT_JS_APP_ROUTER = "NEXT_JS_APP_ROUTER",
  VITE = "VITE",
  // REMIX = "REMIX",
}

interface Props {}

type TabPanelProps = {
  children?: React.ReactNode;
  filename?: string;
};

const TabPanel: React.FC<TabPanelProps> = React.memo(
  ({ children, filename }) => {
    const ref = React.useRef<HTMLElement>(null);

    React.useEffect(() => {
      if (ref.current) {
        hljs.highlightBlock(ref.current);
      }
    }, [children]);

    return (
      <Box p={3} className="tab-panel">
        <div className="code-container">
          {filename && <div className="filename-box">{filename}</div>}
          <pre>
            <code ref={ref} className="javascript select-text">
              {children}
            </code>
          </pre>
        </div>
      </Box>
    );
  }
);

const InstallTempoModal: React.FC<Props> = React.memo(() => {
  const dispatch = useDispatch<any>();
  const canvasId = useCanvasId();

  const { isLocalMode } = useLocalMode();

  const { session } = useSession();

  const [installationPhase, setInstallationPhase] = useState(
    InstallationPhase.INSTALLING
  );

  const [installationError, setInstallationError] = useState(null);

  const [devtoolHover, setDevtoolHover] = useState(false);

  const project = useActiveProject();

  const [tabValue, setTabValue] = useState(InstallationFramework.VITE);
  const [prefilledTabValue, setPrefilledTabValue] = useState(false);

  const [installedNewDevTools, setInstalledNewDevTools] = useState(false);
  const [installingNewDevTools, setInstallingNewDevTools] = useState(false);

  const [gitignoreSetup, setGitignoreSetup] = useState(false);

  const devtoolStatus = useCodebaseStore((state) => state.devtoolStatus);
  const devtoolVersion = useCodebaseStore((state) => state.devtoolVersion);
  const conditions = useCodebaseStore((state) => state.conditions);

  const setNewLayout = useLayoutStore((state) => state.setNewLayout);

  const [numTimesInstalledWithAi, setNumTimesInstalledWithAi] = useState(0);
  // Every time this component shows up, reset it
  useEffect(() => {
    setNumTimesInstalledWithAi(0);
  }, []);

  /**
   * Conditions for loading the app
   */
  const devtoolCondition = conditions["devtoolInitialized"];
  const babelPluginInstalled = conditions["babelPluginInstalled"];
  const swcPluginInstalled = conditions["swcPluginInstalled"];
  const anyPluginInstalled =
    babelPluginInstalled?.met || swcPluginInstalled?.met;

  const devtoolsInitialized =
    devtoolCondition !== undefined &&
    devtoolCondition.met &&
    anyPluginInstalled;

  const packageInstalled =
    devtoolStatus === DevtoolStatus.Latest ||
    devtoolStatus === DevtoolStatus.Development;

  const appReadyToLaunch =
    devtoolsInitialized && packageInstalled && gitignoreSetup;

  useEffect(() => {
    if (installationPhase === InstallationPhase.INSTALLING) {
      return;
    }

    if (appReadyToLaunch) {
      startServer({
        forceRestart: true,
      });
      setShowDevtoolModal(false);
    }
  }, [appReadyToLaunch, installationPhase]);

  useEffect(() => {
    if (prefilledTabValue) {
      return;
    }

    if (
      project?.framework === ReactFramework.VITE_WITH_VIKE ||
      project?.framework === ReactFramework.VITE ||
      project?.framework === ReactFramework.VITE_REACT_ROUTER_V7
    ) {
      setTabValue(InstallationFramework.VITE);
      setPrefilledTabValue(true);
    } else if (project?.framework === ReactFramework.NEXT_JS) {
      setTabValue(InstallationFramework.NEXT_JS);
      setPrefilledTabValue(true);
    } else if (project?.framework === ReactFramework.NEXT_JS_APP_ROUTER) {
      setTabValue(InstallationFramework.NEXT_JS_APP_ROUTER);
      setPrefilledTabValue(true);
    }
  }, [project, prefilledTabValue]);

  const projectLaunchStatus = useCodebaseStore(
    (state) => state.projectLaunchStatus
  );
  const openFile = useCodebaseStore((state) => state.openFile);
  const revealLocation = useCodebaseStore((state) => state.revealLocation);
  const updateLookupsBasedOnPartialResponse = useCodebaseStore(
    (state) => state.updateLookupsBasedOnPartialResponse
  );
  const setShowDevtoolModal = useCodebaseStore(
    (state) => state.setShowDevtoolModal
  );

  const startServer = useStartServer();

  const isChatLoading = useIsChatLoading();
  const setSendChatMessage = useAiContextStore(
    (state) => state.setSendChatMessage
  );

  useEffect(() => {
    if (devtoolStatus < DevtoolStatus.Latest) {
      setInstallationPhase(InstallationPhase.INSTALLING);
    } else {
      setInstallationPhase(InstallationPhase.INITIALIZING);
    }
  }, [devtoolStatus, conditions]);

  useEffect(() => {
    if (!conditions) {
      return;
    }

    if (conditions && conditions["gitignoreSetup"]?.met) {
      setGitignoreSetup(true);
      return;
    } else {
      setGitignoreSetup(false);
    }
  }, [conditions]);

  const handleInstall = () => {
    if (installingNewDevTools || !session) {
      return;
    }

    setDevtoolHover(false);
    setInstallingNewDevTools(true);
    getUnexpiredToken(session).then((supabaseJWT) => {
      axios
        .post(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/canvases/${canvasId}/writeLatestDevtoolToProject`,
          {},
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          }
        )
        .then((response) => {
          updateLookupsBasedOnPartialResponse(response.data);
          dispatch(
            fetchGitStatus({
              session,
              canvasId,
            })
          );
          openFile({
            file: {
              filename: response.data.filename,
              diffType: true,
              context: EditorContext.Code,
            },
            jumpToTab: true,
          });
          setInstalledNewDevTools(true);
          setInstallingNewDevTools(false);
        })
        .catch((error) => {
          setInstallationError(error.reason);
          setInstallingNewDevTools(false);
        });
    });
    setInstallationPhase(InstallationPhase.INITIALIZING);
  };

  const devtoolDependency = useMemo(() => {
    switch (devtoolStatus) {
      case DevtoolStatus.NotInstalled:
        return (
          <>
            <div
              className={`dependency-row devtool uninstalled`}
              onMouseEnter={() =>
                !installingNewDevTools && setDevtoolHover(true)
              }
              onMouseLeave={() => setDevtoolHover(false)}
              onClick={handleInstall}
            >
              {installingNewDevTools ? (
                <div>Installing...</div>
              ) : devtoolHover ? (
                <div>Install</div>
              ) : (
                <>
                  <div>tempo-devtools</div>
                  <div>Not installed</div>
                </>
              )}
            </div>
          </>
        );
      case DevtoolStatus.Outdated:
        return (
          <>
            <div
              className={`dependency-row devtool outdated`}
              onMouseEnter={() =>
                !installingNewDevTools && setDevtoolHover(true)
              }
              onMouseLeave={() => setDevtoolHover(false)}
              onClick={handleInstall}
            >
              {installingNewDevTools ? (
                <div>Upgrading...</div>
              ) : devtoolHover ? (
                <div>Upgrade</div>
              ) : (
                <>
                  <div>tempo-devtools</div>
                  <div>{devtoolVersion}</div>
                </>
              )}
            </div>
          </>
        );
      case DevtoolStatus.Latest:
        return (
          <div className="dependency-row devtool latest">
            <div>tempo-devtools</div>
            <div>{devtoolVersion}</div>
          </div>
        );
      case DevtoolStatus.Development:
        return (
          <div className="dependency-row devtool development">
            <div>tempo-devtools</div>
            <div>Development</div>
          </div>
        );
    }
  }, [devtoolStatus, devtoolHover, projectLaunchStatus]);

  const gitignoreComponent = useMemo(() => {
    if (!gitignoreSetup) {
      return (
        <div className="flex flex-col space-y-1 p-1">
          <p className="text-sm text-gray-400">
            Create or add the following to your .gitignore:
          </p>
          <TabPanel filename=".gitignore">
            {`# Tempo

node_modules/
${
  project?.framework === ReactFramework?.REMIX ||
  project?.framework === ReactFramework?.VITE_REACT_ROUTER_V7
    ? `**/routes/tempobook.dynamic.*
**/routes/tempobook.storyboards.*`
    : `**/tempobook/dynamic/
**/tempobook/storyboards/`
}
`}
          </TabPanel>
          <div className="flex justify-end">
            <div
              className="border-b border-dashed border-gray-400 text-gray-400 text-xs inline-block cursor-pointer"
              onClick={() => {
                dispatch(
                  setCursorType({
                    cursorType: CursorType.Pointer,
                  })
                );
                // @ts-expect-error TS2345
                setNewLayout(LeftTab.FileSelector, null);
              }}
            >
              Open code
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="dependency-row devtool latest">
          <div>.gitignore</div>
          <div>Setup</div>
        </div>
      );
    }
  }, [gitignoreSetup, projectLaunchStatus]);

  const initializationSnippet = () => {
    if (devtoolsInitialized) {
      const { data } = conditions["devtoolInitialized"];
      const { filename, lineNumber, characterPosition } = data || {};

      let parsedNumber, handleClickLink, parsedFileBase;
      if (data) {
        parsedNumber = Number.parseInt(lineNumber);
        if (Number.isNaN(parsedNumber)) {
          parsedNumber = 0;
        }
        parsedFileBase = filename.split("/").pop();

        handleClickLink = () => {
          openFile({
            file: {
              filename: filename,
              context: EditorContext.Code,
            },
            jumpToTab: true,
          });
          revealLocation({
            revealLocationAt: characterPosition,
          });
        };
      }

      return (
        <>
          <h3 className="card-installation-phase text-sm font-extrabold">
            Initialization
          </h3>
          <div className="dependency-row devtool latest">
            <div>Devtools Initialized</div>
            {data && (
              <div className="file-link" onClick={handleClickLink}>
                See {parsedFileBase}: {lineNumber}
              </div>
            )}
          </div>
        </>
      );
    } else {
      return (
        <>
          <h3 className="text-md text-white font-extrabold">Setup</h3>
          <p className="card-instruction">
            The dev tools allow Tempo to connect the visual editor to your code.
            You can use the file selector in the left navbar to select files to
            edit.
          </p>
          <Tabs
            value={tabValue}
            onChange={(_, newValue: any) => setTabValue(newValue)}
            textColor="inherit"
          >
            <Tab
              className="tab"
              label="Vite"
              disableRipple
              value={InstallationFramework.VITE}
            />
            <Tab
              className="tab"
              label="Next.js (Pages Router)"
              disableRipple
              value={InstallationFramework.NEXT_JS}
            />
            <Tab
              className="tab"
              label="Next.js (App Router)"
              disableRipple
              value={InstallationFramework.NEXT_JS_APP_ROUTER}
            />
            {/* <Tab className="tab" label="Remix" disableRipple value="REMIX" /> */}
          </Tabs>

          {/* {tabValue === InstallationFramework.CRA && (
            <>
              <h3 className="pt-3 text-md text-[grey] font-extrabold">
                Plugin
              </h3>
              <h3 className="card-instruction pt-2">
                Add the following babel plugin (example using customize-cra and
                react-app-rewired)
              </h3>
              <TabPanel filename="config-overrides.js">
                {`const { addBabelPlugins, override } = require('customize-cra');
module.exports = override(...addBabelPlugins('tempo-devtools/dist/babel-plugin'));
`}
              </TabPanel>
            </>
          )} */}

          {(tabValue === InstallationFramework.NEXT_JS ||
            tabValue === InstallationFramework.NEXT_JS_APP_ROUTER) && (
            <>
              <h3 className="pt-3 text-md text-[grey] font-extrabold">
                Error Handling
              </h3>
              {tabValue === InstallationFramework.NEXT_JS && (
                <>
                  <h3 className="card-instruction">
                    To help Tempo catch and display errors add the following
                    script to the body of your `_document.tsx` file:
                  </h3>
                  <TabPanel filename="pages/_document.tsx">
                    {`
import Script from "next/script";

export default function Document() {
  return (
    <Html>
      <Head>
        <Script src="https://api.tempo.build/proxy-asset?url=https://storage.googleapis.com/tempo-public-assets/error-handling.js" />
      </Head>
    
      ...etc
    </Html>
  );
}`}
                  </TabPanel>
                </>
              )}

              {tabValue === InstallationFramework.NEXT_JS_APP_ROUTER && (
                <>
                  <h3 className="card-instruction">
                    To help Tempo catch and display errors add the following
                    script to the body of your `layout.tsx` file:
                  </h3>
                  <TabPanel filename="app/layout.tsx">
                    {`
import Script from "next/script";

export default function MainLayout() {
  return (
    <html>
      <Script src="https://api.tempo.build/proxy-asset?url=https://storage.googleapis.com/tempo-public-assets/error-handling.js" />

      ...etc
    </html>
  );
}
`}
                  </TabPanel>
                </>
              )}
            </>
          )}

          {tabValue === InstallationFramework.VITE && (
            <>
              <h3 className="pt-3 text-md text-[grey] font-extrabold">
                Error Handling
              </h3>
              <h3 className="card-instruction">
                To help Tempo catch and display errors add the following script
                to the body of your `index.html` file:
              </h3>
              <TabPanel filename="index.html">
                {`<!doctype html>
<html lang="en">
  <head>
    //...
  </head>
  <body>
    //...
    <script src="https://api.tempo.build/proxy-asset?url=https://storage.googleapis.com/tempo-public-assets/error-handling.js"></script>
  </body>
</html>
`}
              </TabPanel>

              <h3 className="pt-3 text-md text-[grey] font-extrabold">
                Routing
              </h3>
              <h3 className="card-instruction">
                To allow Tempo to route to storyboards, add the following to
                your react router routes: These routes are only included if the
                `VITE_TEMPO` environment variable is set to `true`, so they will
                not be included in your production build.
              </h3>
              <TabPanel filename="App.tsx">
                {`// ...Other imports
import { useRoutes } from "react-router-dom";
import routes from "tempo-routes";

    // Tempo routes
    {import.meta.env.VITE_TEMPO && useRoutes(routes)}
    <Routes>
      // ...Routes for your app
    </Routes>
`}
              </TabPanel>
              <h3 className="card-instruction">
                Please note, if you have a catchall route `*` in your routes,
                you need to allow tempo to capture the routes before the
                catchall route.
              </h3>
              <TabPanel filename="App.tsx">
                {`
<Routes>
  <Route path="/some-route" element={<Home />}/>
  <Route path="*" element={<Navigate to="login" />} />
</Routes>


// Would change to


// For the tempo routes
{import.meta.env.VITE_TEMPO && useRoutes(routes)}

<Routes>
  <Route path="/some-route" element={<Home />}/>

  // Add this before the catchall route
  {import.meta.env.VITE_TEMPO && <Route path="/tempobook/*" />}

  <Route path="*" element={<Navigate to="login" />} />
</Routes>
`}
              </TabPanel>

              <h3 className="pt-3 text-md text-[grey] font-extrabold">
                Plugin
              </h3>
              <TabPanel filename="vite.config.ts">
                {`import { defineConfig } from 'vite';
import { tempo } from "tempo-devtools/dist/vite"; // Add this import


// https://vitejs.dev/config/
export default defineConfig({
  plugins: [
    tempo() // Add the tempo plugin
  ],
  server: {
    // @ts-ignore
    allowedHosts: process.env.TEMPO === "true" ? true : 
      undefined // Whatever was the previous value or undefined
  }
});`}
              </TabPanel>
            </>
          )}

          {/* {tabValue === InstallationFramework.REMIX && (
            <>
              <h3 className="pt-3 text-md text-[grey] font-extrabold">
                Plugin
              </h3>
              <h3 className="card-instruction pt-2">
                Plugin not supported yet
              </h3>
            </>
          )} */}
        </>
      );
    }
  };

  return (
    <div className="install-tempo-modal modal-container">
      <div className="card fixed">
        <h2 className="pb-4 text-lg text-white font-extrabold">Setup Tempo</h2>

        <div className="w-full gap-2 flex flex-row justify-between">
          <div className="flex flex-col">
            <h3 className="text-md text-white font-extrabold">
              AI Install (Beta)
            </h3>

            <p className="card-instruction">
              Tempo can automatically install the devtools for you.
            </p>
          </div>

          <div className="flex flex-col">
            <Button
              variant="primary"
              disabled={isChatLoading}
              onClick={() => {
                if (numTimesInstalledWithAi >= 5) {
                  show();
                  return;
                }

                setSendChatMessage({
                  sendChatMessage:
                    "Perform all the steps to set this project up for Tempo. Including (if needed): installing tempo-devtools, adding to the gitignore, setting up error handling, changing the plugin and configuring routing.",
                  isFreePrompt: true,
                });
                // @ts-expect-error TS2345
                setNewLayout(LeftTab.AIChat, null);
                setNumTimesInstalledWithAi(numTimesInstalledWithAi + 1);

                track("AI_PROMPT_REQUESTED", {
                  type: "INSTALL_WITH_AI",
                  isFreePrompt: true,
                });
              }}
            >
              {numTimesInstalledWithAi >= 5
                ? "Get Help Installing"
                : "Install with AI"}
            </Button>
          </div>
        </div>

        <div className="max-h-[400px] overflow-scroll gap-2 flex flex-col">
          <h3 className="text-md text-white font-extrabold">Installation</h3>

          {installationPhase === InstallationPhase.INSTALLING && (
            <p className="card-instruction">
              Installing Tempo Devtools adds a few devDependencies and project
              specific environment variables to integrate with Tempo.
            </p>
          )}

          {devtoolDependency}

          {installationPhase === InstallationPhase.INITIALIZING &&
            initializationSnippet()}
          {installationPhase === InstallationPhase.INITIALIZING && (
            <div>
              <h3 className="pb-2 text-sm text-white font-extrabold">Setup</h3>
              {gitignoreComponent}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default InstallTempoModal;
